import React, { useState, useEffect, useCallback } from 'react';

interface Plan {
  id: number;
  name: string;
  price: number;
  concurrent_calls: number;
  monthly_calls: number | null;
  description?: string;
  features?: string[];
}

interface ContractServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  plans: Plan[];
  onContract: (planId: number, serviceName: string, serviceDescription: string, billingCycle: string, paymentMethod: string) => Promise<void>;
  isLoading: boolean;
  isLoadingPlans?: boolean;
}

const ContractServiceModal: React.FC<ContractServiceModalProps> = ({
  isOpen,
  onClose,
  plans,
  onContract,
  isLoading,
  isLoadingPlans = false
}) => {
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [serviceName, setServiceName] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [paymentMethod, setPaymentMethod] = useState('iugupix');

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([1])); // Start with step 1 expanded

  const totalSteps = 3;

  const toggleStep = (stepNumber: number) => {
    const newExpanded = new Set(expandedSteps);
    if (newExpanded.has(stepNumber)) {
      newExpanded.delete(stepNumber);
    } else {
      newExpanded.add(stepNumber);
    }
    setExpandedSteps(newExpanded);
  };


  const resetForm = useCallback(() => {
    setSelectedPlan(null);
    setServiceName('');
    setServiceDescription('');
    setBillingCycle('monthly');
    setPaymentMethod('iugupix');
    setCurrentStep(1);
    setExpandedSteps(new Set([1]));
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const handlePlanSelect = (planId: number) => {
    setSelectedPlan(planId);
    // Close step 1 and open step 2
    setExpandedSteps(new Set([2]));
    setCurrentStep(2);
  };

  const handleServiceConfigComplete = () => {
    if (serviceName.trim()) {
      // Go to step 3
      setExpandedSteps(new Set([3]));
      setCurrentStep(3);
    }
  };


  const handleContract = async () => {
    if (!selectedPlan || !serviceName.trim()) return;

    await onContract(selectedPlan, serviceName.trim(), serviceDescription.trim(), billingCycle, paymentMethod);
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="h-full overflow-y-auto flex items-start justify-center pt-6 pb-6 px-4">
        <div className="w-full max-w-7xl bg-gradient-to-br from-white via-gray-50 to-blue-50 rounded-3xl shadow-2xl border border-gray-200/50 mx-auto relative overflow-hidden z-[110]">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-cyan-100/30 to-blue-100/30 rounded-full -translate-y-48 translate-x-48"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-purple-100/20 to-pink-100/20 rounded-full translate-y-40 -translate-x-40"></div>

          <div className="relative z-10 p-8">
            <div className="flex items-center justify-between mb-8">
              <div className="flex-1">
                <div className="flex items-center mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mr-4">
                    <span className="text-white text-xl">📞</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                      Contratar Serviço VoIP
                    </h2>
                    <div className="w-12 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full mt-1"></div>
                  </div>
                </div>
                <p className="text-gray-600 text-base leading-relaxed">
                  Siga os passos abaixo para contratar seu serviço VoIP. Cada etapa é simples e guiada.
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-3 rounded-xl transition-all duration-200 hover:scale-110 ml-4"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-center mb-4">
                {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
                  <div key={step} className="flex items-center">
                    <div
                      className={`w-4 h-4 rounded-full transition-all duration-200 ${
                        step <= currentStep
                          ? 'bg-cyan-500 shadow-lg'
                          : 'bg-gray-200'
                      }`}
                    />
                    {step < totalSteps && (
                      <div className={`w-16 h-0.5 mx-3 ${
                        step < currentStep ? 'bg-cyan-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
              <div className="text-center">
                <span className="text-sm text-gray-600">
                  {currentStep === 1 ? 'Escolha seu plano' :
                   currentStep === 2 ? 'Personalize seu serviço' :
                   'Configure pagamento'}
                </span>
              </div>
            </div>

            {/* Step 1: Choose Plan */}
            <div className="mb-6">
              <button
                onClick={() => toggleStep(1)}
                className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all duration-200 ${
                  currentStep === 1
                    ? 'border-cyan-500 bg-cyan-50'
                    : selectedPlan
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3 ${
                    selectedPlan ? 'bg-green-500 text-white' : currentStep === 1 ? 'bg-cyan-500 text-white' : 'bg-gray-400 text-white'
                  }`}>
                    {selectedPlan ? '✓' : ''}
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-gray-900">Escolha seu Plano</h3>
                    <p className="text-sm text-gray-600">
                      {selectedPlan ? 'Plano selecionado ✓' : 'Selecione um plano para continuar'}
                    </p>
                  </div>
                </div>
                <svg
                  className={`w-5 h-5 transition-transform duration-200 ${
                    expandedSteps.has(1) ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {expandedSteps.has(1) && (
                <div className="mt-4 p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                  {isLoadingPlans && plans.length === 0 ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-200 border-t-cyan-600 mx-auto mb-6"></div>
                        <p className="text-gray-600 text-base font-medium">Carregando planos incríveis...</p>
                        <p className="text-gray-500 text-xs mt-2">Aguarde enquanto preparamos as melhores opções para você</p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                      {plans.map((plan) => (
                        <div
                          key={plan.id}
                          className={`relative group border-2 rounded-2xl p-6 cursor-pointer transition-all duration-300 hover:shadow-xl ${
                            selectedPlan === plan.id
                              ? 'border-cyan-500 bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 ring-4 ring-cyan-200/50 shadow-2xl transform scale-[1.02]'
                              : 'border-gray-200/60 hover:border-cyan-300 hover:shadow-lg hover:transform hover:scale-[1.01] bg-white/80 backdrop-blur-sm'
                          }`}
                          onClick={() => handlePlanSelect(plan.id)}
                        >
                          <div className="text-center">
                            <h4 className="text-lg font-bold text-gray-900 mb-2">
                              {plan.name}
                            </h4>
                            <div className="mb-4">
                              <div className="flex items-baseline justify-center gap-1">
                                <span className="text-lg font-bold text-cyan-600">R$</span>
                                <span className="text-3xl font-bold text-cyan-600">
                                  {parseFloat(plan.price.toString()).toFixed(2).replace('.', ',')}
                                </span>
                              </div>
                              <div className="text-center">
                                <span className="text-xs text-gray-500">/mês</span>
                              </div>
                            </div>
                            <div className="space-y-2 text-xs text-gray-700">
                              <div className="flex items-center justify-center bg-green-50 rounded-lg p-2">
                                <span className="font-medium">{plan.concurrent_calls} chamadas simultâneas</span>
                              </div>
                              <div className="flex items-center justify-center bg-green-50 rounded-lg p-2">
                                <span className="font-medium">
                                  {plan.monthly_calls === null
                                    ? 'Ligações ilimitadas'
                                    : `${plan.monthly_calls} ligações/mês`}
                                </span>
                              </div>
                              {plan.features && plan.features.slice(0, 2).map((feature: string, index: number) => (
                                <div key={index} className="flex items-center justify-center bg-green-50 rounded-lg p-2">
                                  <span className="font-medium">{feature}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          {selectedPlan === plan.id && (
                            <div className="absolute -top-2 -right-2 bg-cyan-500 rounded-full p-2 shadow-lg">
                              <span className="text-white">✓</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 2: Complete Configuration */}
            {selectedPlan && (
              <div className="mb-6">
                <button
                  onClick={() => toggleStep(2)}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all duration-200 ${
                    currentStep === 2
                      ? 'border-cyan-500 bg-cyan-50'
                      : serviceName.trim()
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3 ${
                      serviceName.trim() ? 'bg-green-500 text-white' : currentStep === 2 ? 'bg-cyan-500 text-white' : 'bg-gray-400 text-white'
                    }`}>
                      {serviceName.trim() ? '✓' : ''}
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-semibold text-gray-900">Personalize seu novo serviço VoIP</h3>
                      <p className="text-sm text-gray-600">
                        {serviceName.trim() ? 'Personalização completa ✓' : 'Dê um nome e descrição para seu serviço'}
                      </p>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 transition-transform duration-200 ${
                      expandedSteps.has(2) ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {expandedSteps.has(2) && (
                  <div className="mt-4 p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-gray-800">
                          Nome do Serviço <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          maxLength={30}
                          className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200 bg-white/80 backdrop-blur-sm hover:bg-white"
                          value={serviceName}
                          onChange={(e) => setServiceName(e.target.value)}
                          placeholder="Ex: Ramal Principal"
                        />
                        <div className="flex justify-between items-center mt-1">
                          <p className="text-xs text-gray-500">Nome do serviço (máx. 30 caracteres)</p>
                          <span className={`text-xs ${serviceName.length > 25 ? 'text-orange-500' : 'text-gray-400'}`}>
                            {serviceName.length}/30
                          </span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-semibold text-gray-800">
                          Descrição (Opcional)
                        </label>
                        <input
                          type="text"
                          maxLength={50}
                          className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200 bg-white/80 backdrop-blur-sm hover:bg-white"
                          value={serviceDescription}
                          onChange={(e) => setServiceDescription(e.target.value)}
                          placeholder="Descrição do serviço"
                        />
                        <div className="flex justify-between items-center mt-1">
                          <p className="text-xs text-gray-500">Descrição opcional (máx. 50 caracteres)</p>
                          <span className={`text-xs ${serviceDescription.length > 40 ? 'text-orange-500' : 'text-gray-400'}`}>
                            {serviceDescription.length}/50
                          </span>
                        </div>
                      </div>
                    </div>
                    {serviceName.trim() && (
                      <div className="mt-6 flex justify-end">
                        <button
                          onClick={handleServiceConfigComplete}
                          className="px-6 py-2 bg-cyan-500 text-white text-sm font-semibold rounded-lg hover:bg-cyan-600 transition-colors"
                        >
                          Continuar para Pagamento →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Payment Configuration */}
            {selectedPlan && serviceName.trim() && (
              <div className="mb-6">
                <button
                  onClick={() => toggleStep(3)}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all duration-200 ${
                    currentStep === 3
                      ? 'border-cyan-500 bg-cyan-50'
                      : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3 ${
                      currentStep === 3 ? 'bg-cyan-500 text-white' : 'bg-gray-400 text-white'
                    }`}>
                      {''}
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-semibold text-gray-900">Configuração de Pagamento</h3>
                      <p className="text-sm text-gray-600">Escolha como deseja pagar seu serviço</p>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 transition-transform duration-200 ${
                      expandedSteps.has(3) ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {expandedSteps.has(3) && (
                  <div className="mt-4 space-y-6">
                    {/* Payment Configuration */}
                    <div className="p-6 bg-gradient-to-r from-cyan-50 via-blue-50 to-indigo-50 rounded-2xl border border-cyan-200/60 shadow-lg">
                      <div className="flex items-center mb-6">
                        <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mr-4">
                          <span className="text-white text-sm">💳</span>
                        </div>
                        <div>
                          <h4 className="text-base font-bold text-gray-900">Configuração de Pagamento</h4>
                          <p className="text-gray-600 text-xs">Escolha como deseja pagar seu serviço</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="block text-xs font-semibold text-gray-800">
                            Ciclo de Cobrança
                          </label>
                          <select
                            className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200 bg-white/80 backdrop-blur-sm hover:bg-white"
                            value={billingCycle}
                            onChange={(e) => setBillingCycle(e.target.value)}
                          >
                            <option value="monthly">Mensal</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-xs font-semibold text-gray-800">
                            Método de Pagamento
                          </label>
                          <select
                            className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200 bg-white/80 backdrop-blur-sm hover:bg-white"
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                          >
                            <option value="iugupix">PIX (Recomendado)</option>
                            <option value="stripe" disabled>Cartão de Crédito</option>
                            <option value="boletobanktransfer" disabled>Boleto Bancário</option>
                            <option value="banktransfer" disabled>Transferência Bancária</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Process Information */}
                    <div className="p-6 bg-gradient-to-r from-emerald-50 via-green-50 to-teal-50 rounded-2xl border border-emerald-200/60 shadow-lg">
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mr-4 mt-1">
                          <span className="text-white text-sm">🚀</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-base font-bold text-gray-900 mb-2">Processo de Ativação</h4>
                          <p className="text-gray-600 text-xs mb-4">Seu serviço será ativado automaticamente seguindo estes passos:</p>
                          <div className="space-y-3">
                            <div className="flex items-start">
                              <div className="w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center mr-3 mt-0.5">
                                <span className="text-white text-xs font-bold">1</span>
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">Contratação</p>
                                <p className="text-xs text-gray-600">Criação da fatura no sistema</p>
                              </div>
                            </div>
                            <div className="flex items-start">
                              <div className="w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center mr-3 mt-0.5">
                                <span className="text-white text-xs font-bold">2</span>
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">Pagamento</p>
                                <p className="text-xs text-gray-600">Efetue o pagamento via método escolhido</p>
                              </div>
                            </div>
                            <div className="flex items-start">
                              <div className="w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center mr-3 mt-0.5">
                                <span className="text-white text-xs font-bold">3</span>
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">Configuração</p>
                                <p className="text-xs text-gray-600">Após pagamento, configure IP e WhatsApp</p>
                              </div>
                            </div>
                            <div className="flex items-start">
                              <div className="w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center mr-3 mt-0.5">
                                <span className="text-white text-xs font-bold">4</span>
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">Ativação</p>
                                <p className="text-xs text-gray-600">Ramal será provisionado automaticamente</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end items-center space-x-6 pt-10 border-t border-gray-200/60 mt-10">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-3 border-2 border-gray-300/60 rounded-2xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400/60 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500/50 transition-all duration-200 hover:shadow-lg"
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleContract}
                disabled={!selectedPlan || !serviceName.trim() || isLoading}
                className="px-8 py-3 border border-transparent rounded-2xl shadow-xl text-sm font-bold text-white bg-gradient-to-r from-cyan-600 via-blue-600 to-purple-600 hover:from-cyan-700 hover:via-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 hover:shadow-2xl disabled:transform-none flex items-center"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Contratando...
                  </>
                ) : (
                  <>
                    <span className="mr-2">🚀</span>
                    Contratar Serviço
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractServiceModal;